// ============================================================================
// 📁 FILE: src/modules/ticket/hooks/useSLA.js
// 📝 PURPOSE:
//    Hook to load SLA details for a ticket, periodically refresh, and return
//    normalized SLA structure + loading/error state.
//
// 🔒 Multi-Tenant Safe:
//    Uses activeScope {tenant_id, location_id}
//
// 🔁 Auto Refresh:
//    Refresh SLA every 30 seconds
//
// ============================================================================

import { useState, useEffect, useContext, useCallback } from "react";
import slaService from "../services/slaService";
import { AuthContext } from "../../../context/AuthContext";

export default function useSLA(ticketId) {
  const { activeScope } = useContext(AuthContext);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      if (!ticketId || !activeScope?.tenant_id) return;
      setLoading(true);
      setError(null);

      const data = await slaService.getTicketSLA(ticketId, {
        tenant_id: activeScope?.tenant_id,
        location_id: activeScope?.location_id,
      });

      setSla(data || null);
    } catch (e) {
      console.error("useSLA load failed:", e);
      setError("Failed to load SLA");
    } finally {
      setLoading(false);
    }
  }, [ticketId, activeScope]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (!ticketId) return;
    const id = setInterval(() => {
      load();
    }, 30000);
    return () => clearInterval(id);
  }, [ticketId, load]);

  return {
    sla,
    loading,
    error,
    refreshSLA: load,
  };
}
